export interface TabularSheet {
  name: string;
  headers: string[];
  rows: string[][];
}

export interface TabularColumnMapping {
  latitude: number | null;
  longitude: number | null;
  wkt: number | null;
  label: number | null;
  color: number | null;
  group: number | null;
  order: number | null;
}

export interface TabularTrack {
  points: Array<{ lat: number; lng: number }>;
  name?: string;
  color?: string;
  tool?: 'freehand' | 'line' | 'marker';
}

export interface TabularConversionResult {
  tracks: TabularTrack[];
  acceptedRows: number;
  rejectedRows: number;
  groupedLines: number;
  rejected: Array<{ row: number; reason: 'invalid_coordinates' | 'invalid_wkt' }>;
}

import {
  MAX_DATA_LAYER_FEATURES,
  calculateDataLayerBounds,
  randomDataId,
  type DataFeature,
  type DataLayer,
  type DataLayerSourceFormat,
} from './data-layer.js';

export const MAX_TABULAR_IMPORT_ROWS = MAX_DATA_LAYER_FEATURES;

export interface TabularDataLayerResult {
  layer: DataLayer;
  acceptedRows: number;
  rejectedRows: Array<{ row: number; reason: 'invalid_coordinates' | 'invalid_wkt' }>;
}

const HEADER_ALIASES = {
  latitude: ['latitude', 'lat', 'y', 'pointy', 'ycenter', 'latitude83', 'latdecdeg', 'latdd'],
  longitude: ['longitude', 'lon', 'lng', 'long', 'x', 'pointx', 'xcenter', 'longitude83', 'longdecdeg', 'longdd'],
  wkt: ['wkt', 'geometry', 'geom'],
  label: ['name', 'title', 'label', 'markerlabel'],
  color: ['color', 'markercolor', 'strokecolor'],
  group: ['group', 'route', 'track', 'line', 'lineid', 'routeid', 'trackid'],
  order: ['order', 'sequence', 'seq', 'position', 'sort'],
} as const;

function normalizedHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function findHeader(headers: string[], aliases: readonly string[]): number | null {
  const normalized = headers.map(normalizedHeader);
  const index = normalized.findIndex((header) => aliases.includes(header));
  return index < 0 ? null : index;
}

export function detectTabularColumns(headers: string[]): TabularColumnMapping {
  return {
    latitude: findHeader(headers, HEADER_ALIASES.latitude),
    longitude: findHeader(headers, HEADER_ALIASES.longitude),
    wkt: findHeader(headers, HEADER_ALIASES.wkt),
    label: findHeader(headers, HEADER_ALIASES.label),
    color: findHeader(headers, HEADER_ALIASES.color),
    group: findHeader(headers, HEADER_ALIASES.group),
    order: findHeader(headers, HEADER_ALIASES.order),
  };
}

function parseDelimitedRows(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        value += character;
      }
    } else if (character === '"') {
      quoted = true;
    } else if (character === delimiter) {
      row.push(value);
      value = '';
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      row.push(value);
      if (row.some((cell) => cell.length > 0)) rows.push(row);
      row = [];
      value = '';
    } else {
      value += character;
    }
  }

  row.push(value);
  if (row.some((cell) => cell.length > 0)) rows.push(row);
  return rows;
}

function countDelimiter(line: string, delimiter: string): number {
  let count = 0;
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    if (line[index] === '"') {
      if (quoted && line[index + 1] === '"') index += 1;
      else quoted = !quoted;
    } else if (!quoted && line[index] === delimiter) {
      count += 1;
    }
  }
  return count;
}

export function uniqueTabularHeaders(headers: string[]): string[] {
  const counts = new Map<string, number>();
  return headers.map((header, index) => {
    const base = header.trim() || `Column ${index + 1}`;
    const count = (counts.get(base) ?? 0) + 1;
    counts.set(base, count);
    return count === 1 ? base : `${base} (${count})`;
  });
}

export function parseDelimitedSheet(text: string, name = 'Data', maxRows = MAX_TABULAR_IMPORT_ROWS): TabularSheet {
  const normalizedText = text.replace(/^\uFEFF/, '');
  const sampleLines = normalizedText.split(/\r?\n/).filter(Boolean).slice(0, 5);
  const delimiters = [',', '\t', ';'];
  const headerLine = sampleLines[0] ?? '';
  const delimiter = delimiters
    .map((candidate) => ({ candidate, score: countDelimiter(headerLine, candidate) }))
    .sort((left, right) => right.score - left.score)[0]?.candidate ?? ',';
  const parsed = parseDelimitedRows(normalizedText, delimiter);
  const headers = uniqueTabularHeaders(parsed.shift() ?? []);
  if (parsed.length > maxRows) throw new Error(`This file has more than the ${maxRows.toLocaleString('en-US')} row import limit.`);
  return { name, headers, rows: parsed };
}

function parseNumber(value: string | undefined): number | null {
  if (value === undefined) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = /^-?\d+,\d+$/.test(trimmed) ? trimmed.replace(',', '.') : trimmed;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function coordinatePair(value: string): { lat: number; lng: number } | null {
  const parts = value.trim().split(/\s+/);
  const lng = Number(parts[0]);
  const lat = Number(parts[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
    ? { lat, lng }
    : null;
}

function coordinateList(value: string): Array<{ lat: number; lng: number }> {
  return value.split(',').map(coordinatePair).filter((point): point is { lat: number; lng: number } => point !== null);
}

function unwrapWktBody(value: string, type: string): string | null {
  const match = value.trim().match(new RegExp(`^${type}(?:\\s+Z|\\s+M|\\s+ZM)?\\s*\\((.*)\\)$`, 'i'));
  return match?.[1]?.trim() ?? null;
}

export function parseWkt(value: string): TabularTrack[] {
  const pointBody = unwrapWktBody(value, 'POINT');
  if (pointBody !== null) {
    const point = coordinatePair(pointBody.replace(/[()]/g, ''));
    return point ? [{ points: [point], tool: 'marker' }] : [];
  }

  const lineBody = unwrapWktBody(value, 'LINESTRING');
  if (lineBody !== null) {
    const points = coordinateList(lineBody);
    return points.length > 1 ? [{ points, tool: 'line' }] : [];
  }

  const polygonBody = unwrapWktBody(value, 'POLYGON');
  if (polygonBody !== null) {
    const exteriorRing = polygonBody.match(/^\s*\(([^()]*)\)/)?.[1] ?? polygonBody.replace(/^\(|\)$/g, '');
    const points = coordinateList(exteriorRing);
    return points.length > 2 ? [{ points, tool: 'line' }] : [];
  }

  const multiLineBody = unwrapWktBody(value, 'MULTILINESTRING');
  if (multiLineBody !== null) {
    return [...multiLineBody.matchAll(/\(([^()]*)\)/g)]
      .map((match) => coordinateList(match[1]))
      .filter((points) => points.length > 1)
      .map((points) => ({ points, tool: 'line' as const }));
  }

  const multiPointBody = unwrapWktBody(value, 'MULTIPOINT');
  if (multiPointBody !== null) {
    return multiPointBody.replace(/[()]/g, '').split(',')
      .map(coordinatePair)
      .filter((point): point is { lat: number; lng: number } => point !== null)
      .map((point) => ({ points: [point], tool: 'marker' as const }));
  }

  return [];
}

function cell(row: string[], index: number | null): string {
  return index === null ? '' : (row[index] ?? '').trim();
}

function validColor(value: string): string | undefined {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : undefined;
}

export function convertTabularRows(sheet: TabularSheet, mapping: TabularColumnMapping): TabularConversionResult {
  const tracks: TabularTrack[] = [];
  const groups = new Map<string, Array<{ point: { lat: number; lng: number }; order: number; row: number; name?: string; color?: string }>>();
  let acceptedRows = 0;
  const rejected: TabularConversionResult['rejected'] = [];

  sheet.rows.forEach((row, rowIndex) => {
    const name = cell(row, mapping.label) || undefined;
    const color = validColor(cell(row, mapping.color));
    const wkt = cell(row, mapping.wkt);
    if (wkt) {
      const parsedTracks = parseWkt(wkt);
      if (!parsedTracks.length) {
        rejected.push({ row: rowIndex + 2, reason: 'invalid_wkt' });
        return;
      }
      tracks.push(...parsedTracks.map((track) => ({ ...track, name, color })));
      acceptedRows += 1;
      return;
    }

    const lat = parseNumber(cell(row, mapping.latitude));
    const lng = parseNumber(cell(row, mapping.longitude));
    if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      rejected.push({ row: rowIndex + 2, reason: 'invalid_coordinates' });
      return;
    }

    const point = { lat, lng };
    const group = cell(row, mapping.group);
    if (group) {
      const order = parseNumber(cell(row, mapping.order)) ?? rowIndex;
      const entries = groups.get(group) ?? [];
      entries.push({ point, order, row: rowIndex, name, color });
      groups.set(group, entries);
    } else {
      tracks.push({ points: [point], name, color, tool: 'marker' });
    }
    acceptedRows += 1;
  });

  let groupedLines = 0;
  for (const [name, entries] of groups) {
    entries.sort((left, right) => left.order - right.order || left.row - right.row);
    if (entries.length > 1) {
      tracks.push({ points: entries.map((entry) => entry.point), name, color: entries.find((entry) => entry.color)?.color, tool: 'line' });
      groupedLines += 1;
    } else {
      tracks.push({ points: [entries[0].point], name: entries[0].name || name, color: entries[0].color, tool: 'marker' });
    }
  }

  return { tracks, acceptedRows, rejectedRows: rejected.length, groupedLines, rejected };
}

function rowProperties(headers: string[], row: string[]): Record<string, string> {
  return Object.fromEntries(headers.map((header, index) => [header, row[index] ?? '']));
}

function trackGeometry(track: TabularTrack): DataFeature['geometry'] {
  const positions = track.points.map(({ lng, lat }) => [lng, lat]);
  return track.tool === 'marker'
    ? { type: 'Point', coordinates: positions[0] }
    : { type: 'LineString', coordinates: positions };
}

export function convertTabularSheetToDataLayer(
  sheet: TabularSheet,
  mapping: TabularColumnMapping,
  sourceFormat: DataLayerSourceFormat,
  options: { id?: string; name?: string; position?: number; color?: string } = {},
): TabularDataLayerResult {
  if (sheet.rows.length > MAX_TABULAR_IMPORT_ROWS) {
    throw new Error(`This file has more than the ${MAX_TABULAR_IMPORT_ROWS.toLocaleString('en-US')} row import limit.`);
  }
  const features: DataFeature[] = [];
  const rejectedRows: TabularDataLayerResult['rejectedRows'] = [];
  const groups = new Map<string, Array<{ point: number[]; properties: Record<string, string>; order: number; rowIndex: number }>>();
  let acceptedRows = 0;

  sheet.rows.forEach((row, index) => {
    const properties = rowProperties(sheet.headers, row);
    const wkt = cell(row, mapping.wkt);
    let tracks: TabularTrack[] = [];
    if (wkt) tracks = parseWkt(wkt);
    else {
      const lat = parseNumber(cell(row, mapping.latitude));
      const lng = parseNumber(cell(row, mapping.longitude));
      if (lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const group = cell(row, mapping.group);
        if (group) {
          const entries = groups.get(group) ?? [];
          entries.push({ point: [lng, lat], properties, order: parseNumber(cell(row, mapping.order)) ?? index, rowIndex: index });
          groups.set(group, entries);
          acceptedRows += 1;
          return;
        }
        tracks = [{ points: [{ lat, lng }], tool: 'marker' }];
      }
    }
    if (!tracks.length) {
      rejectedRows.push({ row: index + 2, reason: wkt ? 'invalid_wkt' : 'invalid_coordinates' });
      return;
    }
    const sourceStyle = validColor(cell(row, mapping.color));
    for (const track of tracks) {
      const feature: DataFeature = {
        id: randomDataId('feature'),
        geometry: trackGeometry(track),
        properties: { ...properties },
      };
      if (!wkt && feature.geometry?.type === 'Point' && mapping.latitude !== null && mapping.longitude !== null) {
        feature.coordinateFields = { latitude: sheet.headers[mapping.latitude], longitude: sheet.headers[mapping.longitude] };
      }
      if (sourceStyle) feature.sourceStyle = { color: sourceStyle };
      features.push(feature);
    }
    acceptedRows += 1;
  });

  for (const [group, entries] of groups) {
    entries.sort((left, right) => left.order - right.order || left.rowIndex - right.rowIndex);
    if (entries.length < 2) {
      const entry = entries[0];
      const feature: DataFeature = { id: randomDataId('feature'), geometry: { type: 'Point', coordinates: entry.point }, properties: { ...entry.properties } };
      if (mapping.latitude !== null && mapping.longitude !== null) {
        feature.coordinateFields = { latitude: sheet.headers[mapping.latitude], longitude: sheet.headers[mapping.longitude] };
      }
      features.push(feature);
    } else {
      features.push({
        id: randomDataId('feature'),
        geometry: { type: 'LineString', coordinates: entries.map((entry) => entry.point) },
        properties: { ...entries[0].properties, __group: group },
      });
    }
  }

  const layerId = options.id ?? randomDataId('layer');
  const color = options.color ?? '#5b4bff';
  const layer: DataLayer = {
    id: layerId,
    name: options.name ?? sheet.name,
    sourceFormat,
    style: { color, width: 3, opacity: 1, fillColor: color, fillOpacity: 0.2 },
    visible: true,
    position: options.position ?? 0,
    featureCount: features.length,
    bounds: calculateDataLayerBounds(features),
    saved: false,
    features,
  };
  return { layer, acceptedRows, rejectedRows };
}
